/**
 * Burbuja de corrección junto a un campo ("Solo se permiten números", "El
 * horario disponible es de 08:00 a 17:00"...). Vive en <body> (como la
 * burbuja de días llenos del calendario) para no quedar recortada por
 * contenedores con overflow, y se cierra sola. Una sola a la vez.
 */
let active: HTMLElement | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
// Es position:fixed: al desplazarse la página (o un contenedor) la burbuja
// acompaña al campo; solo se cierra si el campo sale de la pantalla.
let scrollListening = false;
let activeAnchor: HTMLElement | null = null;
// Si el campo se oculta (cambio de pestaña/sección), la burbuja se cierra.
let visibilityTimer: ReturnType<typeof setInterval> | undefined;
// Tocar fuera del campo y de la burbuja la cierra.
let outsideListening = false;
const onOutside = (event: Event) => {
  const target = event.target as Node | null;
  if (!target || active?.contains(target) || activeAnchor?.contains(target)) return;
  dismissInputBubble();
};
const onScroll = () => {
  if (!active || !activeAnchor) return;
  const rect = activeAnchor.getBoundingClientRect();
  const visible = rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  if (!visible || !activeAnchor.isConnected) { dismissInputBubble(); return; }
  place(active, rect);
};

/** Ubica la burbuja junto al campo (arriba, o abajo si no cabe). */
function place(bubble: HTMLElement, rect: DOMRect): void {
  const box = bubble.getBoundingClientRect();
  const showBelow = rect.top < box.height + 16;
  bubble.classList.toggle('below', showBelow);
  const left = Math.max(8, Math.min(rect.left + 12, window.innerWidth - box.width - 8));
  const top = showBelow ? rect.bottom + 10 : rect.top - box.height - 10;
  bubble.style.left = `${left}px`;
  bubble.style.top = `${Math.max(8, top)}px`;
  bubble.style.setProperty('--tail-offset', `${Math.min(Math.max(rect.left + 24 - left, 14), box.width - 14)}px`);
}

export function dismissInputBubble(): void {
  if (timer) clearTimeout(timer);
  active?.remove();
  active = null;
  activeAnchor = null;
  if (visibilityTimer) { clearInterval(visibilityTimer); visibilityTimer = undefined; }
  if (outsideListening) {
    document.removeEventListener('pointerdown', onOutside, true);
    outsideListening = false;
  }
  if (scrollListening) {
    window.removeEventListener('scroll', onScroll, true);
    scrollListening = false;
  }
}

export function showInputBubble(anchor: HTMLElement, message: string, tone: 'warning' | 'info' = 'warning'): void {
  dismissInputBubble();
  const rect = anchor.getBoundingClientRect();
  if (!rect.width && !rect.height) return;
  const bubble = document.createElement('div');
  // Clases propias (is-*): .warning/.info son clases globales de etiquetas en el panel.
  bubble.className = `app-input-bubble is-${tone}`;
  bubble.setAttribute('role', 'status');
  bubble.setAttribute('aria-live', 'polite');
  bubble.textContent = message;
  document.body.appendChild(bubble);
  place(bubble, rect);
  requestAnimationFrame(() => bubble.classList.add('show'));
  active = bubble;
  activeAnchor = anchor;
  visibilityTimer = setInterval(() => {
    const r = anchor.getBoundingClientRect();
    if (!anchor.isConnected || (!r.width && !r.height)) dismissInputBubble();
  }, 250);
  // Textos más largos (ayudas) se quedan más tiempo: 3,2 s a 7 s.
  timer = setTimeout(dismissInputBubble, Math.min(7000, Math.max(3200, message.length * 55)));
  if (!scrollListening) {
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    scrollListening = true;
  }
  // Se registra después del clic que la abrió (ese mismo clic no la cierra).
  setTimeout(() => {
    if (active !== bubble || outsideListening) return;
    document.addEventListener('pointerdown', onOutside, true);
    outsideListening = true;
  });
}
